import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, WS_URL, TOKEN_KEY } from "@/lib/api";
import { SOUNDS } from "@/lib/sounds";

const ICE = { iceServers: [{ urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302", "stun:stun.cloudflare.com:3478"] }], iceCandidatePoolSize: 2, bundlePolicy: "max-bundle", rtcpMuxPolicy: "require" };
let iceExp = 0;
const freshIce = async () => {
  if (Date.now() < iceExp && ICE.iceServers.length > 1) return ICE;
  try {
    const { data } = await api.get("/ice-servers");
    if (data?.ice_servers?.length) { ICE.iceServers = data.ice_servers; iceExp = Date.now() + (data.ttl ? Math.min(data.ttl, 3600) * 500 : 5 * 60 * 1000); }
  } catch (e) { /* keep previous */ }
  return ICE;
};

// Low-bandwidth tuning: cap bitrates so 2G/3G / weak Wi-Fi still carries clear audio first, video second.
const AUDIO_KBPS = 24;
const VIDEO_KBPS = { good: 450, weak: 220, bad: 120 };
const tuneSenders = async (pc, quality = "good") => {
  for (const s of pc.getSenders()) {
    if (!s.track) continue;
    try {
      const p = s.getParameters();
      if (!p.encodings || !p.encodings.length) p.encodings = [{}];
      if (s.track.kind === "audio") { p.encodings[0].maxBitrate = AUDIO_KBPS * 1000; p.encodings[0].priority = "high"; p.encodings[0].networkPriority = "high"; }
      else { p.encodings[0].maxBitrate = VIDEO_KBPS[quality] * 1000; p.encodings[0].maxFramerate = quality === "bad" ? 10 : 15; p.encodings[0].scaleResolutionDownBy = quality === "bad" ? 3 : quality === "weak" ? 2 : 1; p.degradationPreference = "maintain-framerate"; }
      await s.setParameters(p);
    } catch (e) { /* unsupported on this browser */ }
  }
};
const preferOpusLowBw = (sdp) => sdp.replace(/(a=fmtp:\d+ .*?useinbandfec=1)(.*)/g, "$1;stereo=0;maxaveragebitrate=24000;cbr=0;usedtx=1$2");

export function useCallSocket(user) {
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const localRef = useRef(null);
  const pendingRef = useRef({ offer: null, ice: [] });
  const handlerRef = useRef(null);
  const outboxRef = useRef([]);
  const CRITICAL_MSGS = ["call_request", "call_customer", "accept", "reject", "hangup", "chat", "gift"];
  const [connected, setConnected] = useState(false);
  const [call, setCall] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [muted, setMuted] = useState(false);
  const [camOff, setCamOff] = useState(false);
  const [mediaState, setMediaState] = useState("new");
  const [presence, setPresence] = useState({});
  const [chats, setChats] = useState({});
  const [coinsLeft, setCoinsLeft] = useState(null);
  const [giftEvent, setGiftEvent] = useState(null);
  const [event, setEvent] = useState(null);
  const [netIssue, setNetIssue] = useState(null);
  const [permState, setPermState] = useState(null);
  const callIdRef = useRef(null);
  const mediaSentRef = useRef(false);

  const send = useCallback((msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) { ws.send(JSON.stringify(msg)); return; }
    if (CRITICAL_MSGS.includes(msg.type)) outboxRef.current.push(msg);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cleanupMedia = useCallback(() => {
    if (pcRef.current) pcRef.current.close();
    pcRef.current = null;
    if (localRef.current) localRef.current.getTracks().forEach((t) => t.stop());
    localRef.current = null;
    pendingRef.current = { offer: null, ice: [] };
    setLocalStream(null);
    setRemoteStream(null);
    setMuted(false);
    setCamOff(false);
    setMediaState("new");
  }, []);

  const flushIce = async (pc) => {
    for (const c of pendingRef.current.ice) {
      try { await pc.addIceCandidate(c); } catch (e) { /* ignore */ }
    }
    pendingRef.current.ice = [];
  };

  const answerOffer = useCallback(async (pc, offer) => {
    await pc.setRemoteDescription(offer);
    const ans = await pc.createAnswer();
    ans.sdp = preferOpusLowBw(ans.sdp);
    await pc.setLocalDescription(ans);
    send({ type: "answer", payload: ans });
    await flushIce(pc);
    tuneSenders(pc);
  }, [send]);

  const getMedia = async (callType) => {
    const audio = { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1, sampleRate: 48000 };
    const video = callType === "video" ? { facingMode: "user", width: { ideal: 640, max: 960 }, height: { ideal: 480, max: 720 }, frameRate: { ideal: 15, max: 24 } } : false;
    setPermState("asking");
    try { const s = await navigator.mediaDevices.getUserMedia({ audio, video }); setPermState(null); return s; } catch (e) {
      if (callType !== "video") { setPermState("denied"); throw e; }
      try { const s2 = await navigator.mediaDevices.getUserMedia({ audio, video: true }); setPermState(null); return s2; } catch (e2) {
        toast.warning("കാമറ ലഭ്യമല്ല — ഓഡിയോ മാത്രം കണക്റ്റ് ചെയ്യുന്നു");
        try { const s3 = await navigator.mediaDevices.getUserMedia({ audio, video: false }); setPermState(null); return s3; } catch (e3) { setPermState("denied"); throw e3; }
      }
    }
  };

  const setupPeer = useCallback(async (callType, isCaller, reuseMedia = false) => {
    try {
      let stream = reuseMedia && localRef.current && localRef.current.active ? localRef.current : null;
      if (!stream) { stream = await getMedia(callType); localRef.current = stream; setLocalStream(stream); }
      if (pcRef.current) { try { pcRef.current.close(); } catch (e) { /* ignore */ } }
      const pc = new RTCPeerConnection(await freshIce());
      pcRef.current = pc;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      const remote = new MediaStream();
      pc.ontrack = (e) => {
        if (e.streams && e.streams[0]) { setRemoteStream(e.streams[0]); return; }
        remote.addTrack(e.track);
        setRemoteStream(new MediaStream(remote.getTracks()));
      };
      pc.onicecandidate = (e) => e.candidate && send({ type: "ice", payload: e.candidate.toJSON() });
      let restarts = 0;
      const restart = async () => {
        if (!isCaller || pcRef.current !== pc) return;
        if (pc.signalingState !== "stable") { setTimeout(restart, 1500); return; }
        restarts += 1;
        try { const offer = await pc.createOffer({ iceRestart: true }); offer.sdp = preferOpusLowBw(offer.sdp); await pc.setLocalDescription(offer); send({ type: "offer", payload: offer }); } catch (err) { /* ignore */ }
      };
      let quality = "good";
      const adapt = async () => {
        if (pcRef.current !== pc || pc.connectionState !== "connected") return;
        try {
          const stats = await pc.getStats();
          let rtt = 0, loss = 0;
          stats.forEach((r) => {
            if (r.type === "candidate-pair" && r.state === "succeeded" && r.currentRoundTripTime) rtt = Math.max(rtt, r.currentRoundTripTime);
            if (r.type === "remote-inbound-rtp" && typeof r.fractionLost === "number") loss = Math.max(loss, r.fractionLost);
          });
          const q = loss > 0.12 || rtt > 0.7 ? "bad" : loss > 0.04 || rtt > 0.3 ? "weak" : "good";
          if (q !== quality) {
            quality = q; tuneSenders(pc, q); setMediaState(q === "good" ? "connected" : `connected-${q}`);
            if (callIdRef.current) send({ type: "net_quality", call_id: callIdRef.current, poor: q === "bad" });
          }
        } catch (e) { /* ignore */ }
      };
      const adaptTimer = setInterval(adapt, 4000);
      pc.onconnectionstatechange = () => {
        const st = pc.connectionState;
        if (st === "connected") {
          tuneSenders(pc, quality); setMediaState("connected"); toast.dismiss("ice-failed");
          if (callIdRef.current && !mediaSentRef.current) { mediaSentRef.current = true; send({ type: "media_connected", call_id: callIdRef.current }); }
        }
        else if (st === "closed") clearInterval(adaptTimer);
        else setMediaState(st);
        if (st === "failed") { restart(); toast.warning("നെറ്റ്‌വർക്ക് ദുർബലം — വീണ്ടും കണക്റ്റ് ചെയ്യുന്നു, കോൾ തുടരും", { id: "ice-failed", duration: 6000 }); }
        if (st === "disconnected") setTimeout(() => { if (pcRef.current === pc && pc.connectionState === "disconnected") restart(); }, 3000);
      };
      pc.oniceconnectionstatechange = () => { if (pc.iceConnectionState === "failed") restart(); };
      if (isCaller) {
        const offer = await pc.createOffer();
        offer.sdp = preferOpusLowBw(offer.sdp);
        await pc.setLocalDescription(offer);
        send({ type: "offer", payload: offer });
        tuneSenders(pc);
      } else if (pendingRef.current.offer) {
        const o = pendingRef.current.offer;
        pendingRef.current.offer = null;
        await answerOffer(pc, o);
      }
    } catch (e) {
      toast.error("മൈക്ക്/കാമറ അനുമതി ലഭിച്ചില്ല. കോൾ അവസാനിപ്പിക്കുന്നു");
      send({ type: "hangup" });
    }
  }, [send, answerOffer]);

  handlerRef.current = async (d) => {
    switch (d.type) {
      case "call_ringing":
      case "incoming_call":
        freshIce();
        toast.dismiss();
        setCall({ callId: d.call_id, type: d.call_type, peerName: d.peer_name, peerId: d.peer_id, remaining: d.remaining, minutes: d.minutes, elapsed: 0, staffCoins: 0, status: d.type === "incoming_call" ? "incoming" : "ringing" });
        break;
      case "call_started":
        callIdRef.current = d.call_id; mediaSentRef.current = false;
        setCall({ callId: d.call_id, type: d.call_type, peerName: d.peer_name, peerId: d.peer_id, remaining: d.remaining, elapsed: 0, staffCoins: 0, status: "active", billing: false });
        setupPeer(d.call_type, user.role === "customer");
        break;
      case "tick":
        if (d.remaining <= 60 && d.remaining > 55 && user.role === "customer") {
          toast.warning("⏳ 1 മിനിറ്റ് മാത്രം ബാക്കി — കോൾ ഉടൻ അവസാനിക്കും. തുടരാൻ റീചാർജ് ചെയ്യുക.", { id: "low-balance", duration: 8000 });
          try { const ctx = new (window.AudioContext || window.webkitAudioContext)(); const o = ctx.createOscillator(); const g = ctx.createGain(); o.connect(g); g.connect(ctx.destination); o.frequency.value = 880; g.gain.value = 0.08; o.start(); o.stop(ctx.currentTime + 0.25); } catch (e) { /* ignore */ }
        }
        setCall((c) => (c && c.status === "active" ? { ...c, remaining: d.remaining, elapsed: d.elapsed, staffCoins: d.staff_coins } : c));
        break;
      case "call_ended":
        cleanupMedia();
        setCall((c) => ({ ...(c || {}), status: "ended", reason: d.reason, duration: d.duration, remaining: d.remaining, staffCoins: d.staff_coins, refunded: d.refunded || null }));
        setEvent({ t: Date.now(), type: "call_ended" });
        break;
      case "call_resume":
        // our WebSocket dropped mid-call and came back: keep media devices, rebuild the peer connection
        toast.dismiss("ws-reconnecting");
        setCall((c) => ({ ...(c || {}), callId: d.call_id, type: d.call_type, peerName: d.peer_name, peerId: d.peer_id, remaining: d.remaining, elapsed: c?.elapsed || 0, staffCoins: c?.staffCoins || 0, status: "active", billing: true }));
        callIdRef.current = d.call_id; mediaSentRef.current = false;
        pendingRef.current = { offer: null, ice: [] };
        setupPeer(d.call_type, d.is_caller, true);
        break;
      case "peer_reconnecting":
        setMediaState("peer-reconnecting");
        toast.warning(`മറുഭാഗത്ത് നെറ്റ്‌വർക്ക് തടസ്സം — ${d.grace || 45} സെക്കൻഡ് കാത്തിരിക്കുന്നു, കോൾ തുടരും`, { id: "peer-reconnect", duration: 10000 });
        break;
      case "peer_reconnected":
        toast.dismiss("peer-reconnect");
        toast.success("മറുഭാഗം തിരികെ കണക്റ്റ് ആയി", { duration: 3000 });
        break;
      case "billing_started":
        setCall((c) => (c ? { ...c, billing: true } : c));
        break;
      case "net_issue":
        setNetIssue(d.poor ? d.side : null);
        break;
      case "call_reroute":
        toast.info(`🔄 അടുത്ത ലഭ്യമായ staff (${d.staff_name})-ലേക്ക് ഓട്ടോമാറ്റികായി മാറ്റുന്നു — കോൾ വരുന്നു`, { duration: 10000 });
        break;
      case "wallet_adjusted":
        toast.info(`💰 വാലറ്റ് അഡ്ജസ്റ്റ്: ${d.coins >= 0 ? "+" : "-"}₹${Math.abs(Math.round(d.coins / 3))} (അഡ്മിൻ)`, { duration: 8000 });
        setEvent({ t: Date.now(), type: "wallet" });
        break;
      case "auto_refund":
        toast.success(`✅ ${d.staff_name} കോൾ എടുത്തില്ല — ₹${d.inr} ഓട്ടോമാറ്റിക്കായി ബാലൻസിലേക്ക് റീഫണ്ട് ചെയ്തു`, { duration: 10000 });
        setEvent({ t: Date.now(), type: "auto_refund", inr: d.inr });
        break;
      case "package_expired":
        toast.error(`⏳ ${d.staff_name} — ${d.hours} മണിക്കൂറിനുള്ളിൽ വിളിക്കാത്തതിനാൽ ബാക്കി ${Math.ceil((d.remaining || 0) / 60)} മിനിറ്റ് ബാലൻസ് ടൈം ക്യാൻസൽ ആയി`, { duration: 12000 });
        setEvent({ t: Date.now(), type: "package_expired" });
        break;
      case "package_activated":
        toast.info(`▶️ അടുത്ത പാക്കേജ് സജീവമായി — ${d.staff_name} (${Math.ceil((d.remaining || 0) / 60)} മിനിറ്റ് ബാക്കി)`, { duration: 8000 });
        setEvent({ t: Date.now(), type: "package_activated" });
        break;
      case "offer": {
        const pc = pcRef.current;
        if (pc) {
          try {
            if (pc.signalingState === "have-local-offer") { await pc.setLocalDescription({ type: "rollback" }); }
            await answerOffer(pc, d.payload);
          } catch (e) { /* ignore */ }
        } else pendingRef.current.offer = d.payload;
        break;
      }
      case "answer": {
        const pc = pcRef.current;
        if (pc && pc.signalingState === "have-local-offer") { try { await pc.setRemoteDescription(d.payload); await flushIce(pc); } catch (e) { /* ignore */ } }
        break;
      }
      case "ice": {
        const pc = pcRef.current;
        if (pc && pc.remoteDescription) { try { await pc.addIceCandidate(d.payload); } catch (e) { /* ignore */ } } else pendingRef.current.ice.push(d.payload);
        break;
      }
      case "presence":
        setPresence(d.staff || {});
        break;
      case "chat": {
        const peer = d.from_id === user.id ? d.to_id : d.from_id;
        setChats((c) => ({ ...c, [peer]: [...(c[peer] || []), d] }));
        if (d.coins_left !== null && d.coins_left !== undefined) setCoinsLeft(d.coins_left);
        if (d.from_id !== user.id) {
          (user.role === "customer" ? SOUNDS.chatReceiveCustomer : SOUNDS.chatReceiveStaff)();
          if (user.role === "customer") toast(`💬 ${d.from_name}: ${String(d.text).slice(0, 50)}`, { duration: 6000 });
          setEvent({ t: Date.now(), type: "chat", from: d.from_id, name: d.from_name, text: d.text });
        }
        break;
      }
      case "gift":
        setGiftEvent({ ...d, t: Date.now() });
        if (d.coins_left !== null && d.coins_left !== undefined) setCoinsLeft(d.coins_left);
        if (d.from_id !== user.id) toast.success(`${d.from_name} ${d.gift.emoji} ${d.gift.name} ഗിഫ്റ്റ് അയച്ചു!`);
        setEvent({ t: Date.now(), type: "gift" });
        break;
      case "coins_credited":
        toast.success(d.inr ? `₹${d.inr} റീചാർജ് അംഗീകരിച്ചു — ബാലൻസിൽ ക്രെഡിറ്റ് ആയി!` : "ബാലൻസ് ക്രെഡിറ്റ് ആയി!", { duration: 8000 });
        if (d.inr) setGiftEvent({ t: Date.now(), gift: { emoji: "🎁", name: `₹${d.inr} ബാലൻസ്` }, from_name: "RATHRI", title: "പേമെന്റ് സക്സസ്ഫുൾ — റീചാർജ് അംഗീകരിച്ചു" });
        setEvent({ t: Date.now(), type: "coins_credited", inr: d.inr });
        break;
      case "recharge_rejected":
        toast.error(`❌ ₹${d.inr} റീചാർജ് അഭ്യർത്ഥന നിരസിച്ചു — WhatsApp-ൽ ബന്ധപ്പെടുക`, { duration: 8000 });
        setEvent({ t: Date.now(), type: "recharge_rejected" });
        break;
      case "miss_warning":
        toast.warning(`⚠️ തുടർച്ചയായി ${d.streak}/${d.limit} കോളുകൾ എടുത്തില്ല — ${d.limit} ആയാൽ അക്കൗണ്ട് താൽക്കാലികമായി സസ്പെൻഡ് ആകും`, { duration: 10000 });
        break;
      case "account_suspended":
        toast.error(`⛔ അക്കൗണ്ട് സസ്പെൻഡ് ചെയ്തു — ${d.reason}. അഡ്മിനെ ബന്ധപ്പെടുക`, { duration: 15000 });
        setEvent({ t: Date.now(), type: "suspended", reason: d.reason });
        break;
      case "package_selected":
        toast.success(`🔔 ${d.customer_name} നിങ്ങളെ തിരഞ്ഞെടുത്തു — ${d.minutes} മിനിറ്റ് ${d.call_type === "video" ? "വീഡിയോ" : "ഓഡിയോ"}. കോൾ വരുമ്പോൾ എടുക്കുക`, { duration: 15000 });
        setEvent({ t: Date.now(), type: "package_selected", customer_id: d.customer_id, customer_name: d.customer_name });
        break;
      case "fav_online":
        toast.success(`💚 ${d.staff_name} ഇപ്പോൾ ഓൺലൈൻ — ഇപ്പോൾ വിളിക്കാം`, { duration: 8000 });
        break;
      case "presence_set":
        toast.info(`അഡ്മിൻ നിങ്ങളുടെ സ്റ്റാറ്റസ് ${d.presence.toUpperCase()} ആക്കി`);
        setEvent({ t: Date.now(), type: "presence" });
        break;
      case "coins_expiring":
        toast.warning(`⏳ ₹${Math.round((d.coins || 0) / 6)} ബാലൻസ് ${d.days} ദിവസത്തിനുള്ളിൽ കാലാവധി കഴിയും${d.reason === "inactivity_15d" ? " — ഒരു കോൾ/ചാറ്റ് ചെയ്താൽ ടൈമർ പുതുക്കും" : ""}`, { duration: 12000 });
        break;
      case "coins_expired":
        toast.error(`⏳ ₹${Math.round((d.coins || 0) / 6)} ബാലൻസ് കാലാവധി കഴിഞ്ഞു (${d.reason === "validity_30d" ? "30-Day Validity" : "15-Day Inactivity Auto-Cut"})`, { duration: 10000 });
        setEvent({ t: Date.now(), type: "coins" });
        break;
      case "support_reply":
        setEvent({ t: Date.now(), type: "support", text: d.text, attach: d.attach });
        break;
      case "privacy_warning":
        toast.error(d.text, { duration: 7000 });
        break;
      case "duty_reminder":
        toast(`🕒 ${d.minutes} മിനിറ്റിനുള്ളിൽ ഡ്യൂട്ടി (${d.duty_hours}) തുടങ്ങും — ഓൺലൈൻ ആകൂ`, { duration: 10000 });
        break;
      case "missed_customer_online":
        toast.success(`🔁 ${d.customer_name} തിരിച്ചെത്തി — ${d.has_package ? "ഇപ്പോൾ തിരിച്ചു വിളിക്കാം" : "പാക്കേജ് ഇല്ല, ചാറ്റ് ചെയ്യാം"}`, { duration: 12000, action: { label: "Call History", onClick: () => window.dispatchEvent(new CustomEvent("rathri-open-tab", { detail: "missed" })) } });
        SOUNDS.chatReceiveStaff();
        setEvent({ t: Date.now(), type: "missed_customer_online", customer_id: d.customer_id, has_package: d.has_package });
        break;
      case "recharge_reminder":
        toast.warning(`🧾 ₹${d.amount_inr} റീചാർജ് pending — പേമെന്റ് സ്ക്രീൻഷോട്ട് WhatsApp-ൽ അയച്ചോ?`, { duration: 15000 });
        setEvent({ t: Date.now(), type: "recharge_reminder", id: d.id });
        break;
      case "error":
        toast.error(d.message);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!user) return undefined;
    let closed = false;
    let retry = null;
    const connect = () => {
      if (closed) return;
      const ws = new WebSocket(`${WS_URL}?token=${localStorage.getItem(TOKEN_KEY)}`);
      wsRef.current = ws;
      ws.onopen = () => {
        setConnected(true);
        toast.dismiss("ws-reconnecting");
        const q = outboxRef.current;
        outboxRef.current = [];
        q.forEach((m) => { try { ws.send(JSON.stringify(m)); } catch (e) { /* ignore */ } });
      };
      ws.onmessage = (e) => { try { handlerRef.current(JSON.parse(e.data)); } catch (err) { /* ignore */ } };
      ws.onclose = () => {
        setConnected(false);
        if (closed) return;
        if (pcRef.current) toast.loading("നെറ്റ്‌വർക്ക് തടസ്സം — കോൾ നിലനിർത്തി വീണ്ടും കണക്റ്റ് ചെയ്യുന്നു...", { id: "ws-reconnecting", duration: 45000 });
        retry = setTimeout(connect, pcRef.current ? 800 : 1500);
      };
      ws.onerror = () => ws.close();
    };
    connect();
    const revive = () => { if (!closed && document.visibilityState === "visible" && wsRef.current?.readyState !== WebSocket.OPEN) { clearTimeout(retry); try { wsRef.current?.close(); } catch (e) { /* ignore */ } connect(); } };
    document.addEventListener("visibilitychange", revive);
    window.addEventListener("online", revive);
    const ping = setInterval(() => send({ type: "ping" }), 25000);
    return () => { closed = true; clearInterval(ping); clearTimeout(retry); document.removeEventListener("visibilitychange", revive); window.removeEventListener("online", revive); if (wsRef.current) wsRef.current.close(); cleanupMedia(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const loadChat = useCallback((peerId, msgs) => setChats((c) => ({ ...c, [peerId]: msgs })), []);

  return {
    connected, call, presence, chats, coinsLeft, giftEvent, event, localStream, remoteStream, muted, camOff, mediaState, netIssue, loadChat, permState,
    dismissPerm: () => setPermState(null),
    startCall: () => send({ type: "call_request" }),
    callCustomer: (customerId) => send({ type: "call_customer", customer_id: customerId }),
    accept: () => { if (!call || call.status !== "incoming") return; setCall((c) => ({ ...c, status: "connecting" })); send({ type: "accept", call_id: call.callId }); },
    reject: () => send({ type: "reject" }),
    hangup: () => send({ type: "hangup" }),
    closeCall: () => setCall(null),
    sendChat: (to, text) => send({ type: "chat", to, text: text.slice(0, 50) }),
    sendGift: (to, giftId) => send({ type: "gift", to, gift_id: giftId }),
    toggleMute: () => { const m = !muted; (localRef.current?.getAudioTracks() || []).forEach((t) => { t.enabled = !m; }); setMuted(m); },
    toggleCam: () => { const off = !camOff; (localRef.current?.getVideoTracks() || []).forEach((t) => { t.enabled = !off; }); setCamOff(off); },
  };
}
